/**
 * Offline Sync — Flushes the outbox queue when connectivity returns.
 * 
 * Runs on the MAIN THREAD (not in the service worker) so it can reuse
 * the existing monday.js API functions. Works on both iOS and Android:
 *   - Android/Chrome: SW sync event → postMessage → main thread flushOutbox
 *   - iOS/Safari:     window 'online' event → main thread flushOutbox
 */

import { createItem, uploadFile } from './monday'
import { getPendingOutbox, removeFromOutbox, updateOutboxEntry, getOutboxCount } from './offlineStore'
import { syncFormToGHL } from './ghl'

let isFlushing = false
let isInitialized = false
let onSyncCallbacks = []

/**
 * Register a callback to be notified when sync events happen.
 * @param {function} cb — receives { type, entry?, error?, remaining }
 */
export function onSyncEvent(cb) {
    onSyncCallbacks.push(cb)
    return () => { onSyncCallbacks = onSyncCallbacks.filter(c => c !== cb) }
}

function emit(event) {
    onSyncCallbacks.forEach(cb => {
        try { cb(event) } catch {}
    })
}

/**
 * Attempt to flush all pending outbox entries.
 * Skips if already flushing or if offline.
 */
export async function flushOutbox(session = null) {
    if (isFlushing) return
    if (!navigator.onLine) return

    isFlushing = true
    emit({ type: 'sync_start' })

    try {
        const pending = await getPendingOutbox()
        if (pending.length === 0) {
            emit({ type: 'sync_empty' })
            return
        }

        let successCount = 0

        for (const entry of pending) {
            try {
                // Reconstruct File objects from stored ArrayBuffers
                const files = (entry.files || []).map(f =>
                    new File([f.data], f.name, { type: f.type })
                )

                // Create the Monday item
                const result = await createItem(
                    entry.boardId,
                    entry.itemName,
                    entry.columnValues,
                    entry.groupId,
                    session
                )

                // Upload files if any
                if (files.length > 0 && result?.id && entry.fileColumnId) {
                    for (const file of files) {
                        try {
                            await uploadFile(entry.boardId, result.id, entry.fileColumnId, file, session)
                        } catch (fileErr) {
                            console.error('[Sync] File upload error:', fileErr)
                        }
                    }
                }

                // Fire-and-forget GHL sync (if formData was stored)
                if (entry.formType && entry.formData) {
                    syncFormToGHL({ formType: entry.formType, formData: entry.formData }).catch(() => {})
                }

                // Remove from outbox
                await removeFromOutbox(entry.id)
                successCount++

                const remaining = await getOutboxCount()
                emit({ type: 'sync_success', entry, remaining })

            } catch (err) {
                console.error(`[Sync] Failed to send outbox #${entry.id}:`, err)
                await updateOutboxEntry(entry.id, {
                    attempts: (entry.attempts || 0) + 1,
                    lastError: err.message
                })
                emit({ type: 'sync_error', entry, error: err.message })
            }
        }

        // Show notification for synced items
        if (successCount > 0) {
            await showSyncNotification(successCount)
        }

        emit({ type: 'sync_done', sent: successCount, total: pending.length })

    } catch (err) {
        console.error('[Sync] Flush error:', err)
        emit({ type: 'sync_error', error: err.message })
    } finally {
        isFlushing = false
    }
}

/**
 * Show a local notification that queued forms were sent.
 */
async function showSyncNotification(count) {
    if (!('Notification' in window)) return
    if (Notification.permission !== 'granted') return

    try {
        const reg = await navigator.serviceWorker?.getRegistration()
        if (reg) {
            reg.showNotification('JD Rivero', {
                body: count === 1
                    ? 'Your queued form has been submitted successfully.'
                    : `${count} queued forms have been submitted successfully.`,
                icon: '/android-chrome-192x192.png',
                badge: '/favicon-32x32.png',
                tag: 'sync-complete',
                renotify: true
            })
        }
    } catch (err) {
        console.warn('[Sync] Notification error:', err)
    }
}

/**
 * Request notification permission (call from UI interaction).
 * Returns 'granted', 'denied', or 'default'.
 */
export async function requestNotificationPermission() {
    if (!('Notification' in window)) return 'denied'
    if (Notification.permission === 'granted') return 'granted'
    return Notification.permission === 'denied' ? 'denied' : await Notification.requestPermission()
}

/**
 * Initialize the online/offline listeners and SW message handler.
 * Call once on app startup (e.g. in main.jsx or MondayManagement mount).
 */
export function initOfflineSync(session = null) {
    // Guard against duplicate listeners on remount
    if (isInitialized) return
    isInitialized = true

    // iOS fallback: flush when window comes back online
    window.addEventListener('online', () => {
        console.log('[Sync] Back online — flushing outbox')
        flushOutbox(session)
    })

    // Listen for SW messages (Chrome Background Sync triggers this)
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data?.type === 'SYNC_OUTBOX') {
                console.log('[Sync] SW requested outbox flush')
                flushOutbox(session)
            }
        })
    }
}
